import { useEffect, useMemo, useState } from "react";

const RESERVATION_SECTION_IDS = ["calendar", "bookings", "availability", "rules"];
const RESERVATION_STATUSES = ["confirmed", "standby", "cancelled", "completed", "no_show"];
const RESERVATION_SOURCES = ["owner", "member", "walk_in", "event", "system"];

function normalizeReservationSection(value) {
  return RESERVATION_SECTION_IDS.includes(value) ? value : "calendar";
}

function statusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function buildResourceMap(resources = []) {
  return new Map(resources.map((resource) => [resource.resourceId, resource]));
}

function buildChildrenByParentId(resources = []) {
  return resources.reduce((accumulator, resource) => {
    if (!resource.parentResourceId) {
      return accumulator;
    }
    const children = accumulator.get(resource.parentResourceId) || [];
    children.push(resource);
    accumulator.set(resource.parentResourceId, children);
    return accumulator;
  }, new Map());
}

function resourceDepth(resource, resourceById = new Map()) {
  let depth = 0;
  let current = resource;
  const visited = new Set();
  while (
    current?.parentResourceId &&
    resourceById.has(current.parentResourceId) &&
    !visited.has(current.resourceId)
  ) {
    visited.add(current.resourceId);
    current = resourceById.get(current.parentResourceId);
    depth += 1;
  }
  return Math.min(depth, 2);
}

function resourceHierarchyLevel(resource, resourceById = new Map()) {
  if (resource?.type === "lane") {
    return "lane";
  }
  const depth = resourceDepth(resource, resourceById);
  if (depth === 0) {
    return resource?.attributes?.hierarchyLevel === "range" ? "range" : "zone";
  }
  if (depth === 1) {
    return "range";
  }
  return "lane";
}

function hierarchyLevelLabel(level) {
  const labels = {
    zone: "Zone",
    range: "Range",
    lane: "Lane"
  };
  return labels[level] || "Facility";
}

function resourcePathLabel(resource, resourceById = new Map()) {
  const path = [];
  let current = resource;
  const visited = new Set();
  while (current && !visited.has(current.resourceId)) {
    path.unshift(current.name);
    visited.add(current.resourceId);
    current = current.parentResourceId ? resourceById.get(current.parentResourceId) : null;
  }
  return path.join(" / ") || "Facility";
}

function compactResourcePathLabel(resource, resourceById = new Map()) {
  const path = [];
  let current = resource;
  const visited = new Set();
  while (current && !visited.has(current.resourceId)) {
    path.unshift(current.name);
    visited.add(current.resourceId);
    current = current.parentResourceId ? resourceById.get(current.parentResourceId) : null;
  }
  if (path.length >= 2) {
    return `${path[path.length - 1]} · ${path[path.length - 2]}`;
  }
  return path[0] || "Facility";
}

function isArmoryResource(resource = {}) {
  const attributes = resource.attributes || {};
  return String(attributes.assetClass || "").toLowerCase() === "firearm" ||
    Boolean(attributes.firearmMake || attributes.firearmModel || attributes.caliber);
}

function isResourceBookable(resource = {}) {
  return Boolean(resource.reservable && resource.reservationRules?.enabled);
}

function formatDateTime(value) {
  if (!value) {
    return "Not scheduled";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

function formatWindow(item) {
  return `${formatDateTime(item.startsAt)} - ${formatDateTime(item.endsAt)}`;
}

function toDateInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
}

function toDateTimeLocalInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function isoToDateTimeLocalInput(value) {
  return value ? toDateTimeLocalInput(new Date(value)) : "";
}

function durationLabel(startsAt, endsAt) {
  const starts = new Date(startsAt).getTime();
  const ends = new Date(endsAt).getTime();
  if (!Number.isFinite(starts) || !Number.isFinite(ends) || ends <= starts) {
    return "";
  }
  const minutes = Math.round((ends - starts) / 60000);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function reservationCustomerLabel(reservation = {}) {
  return reservation.customerName ||
    reservation.memberSnapshot?.fullName ||
    reservation.contactEmail ||
    "Reservation";
}

function reservationMatchesFilters(reservation, filters, resourceById) {
  const search = String(filters.q || "").trim().toLowerCase();
  const resource = resourceById.get(reservation.resourceId);
  const startsAt = new Date(reservation.startsAt);
  const startTime = startsAt.getTime();
  const fromTime = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).getTime() : null;
  const toTime = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`).getTime() : null;
  const haystack = [
    reservationCustomerLabel(reservation),
    reservation.contactEmail,
    reservation.memberSnapshot?.email,
    reservation.notes,
    reservation.status,
    reservation.source,
    resource?.name,
    resource ? resourcePathLabel(resource, resourceById) : reservation.resourceSnapshot?.name
  ].filter(Boolean).join(" ").toLowerCase();
  return (
    (!filters.status || reservation.status === filters.status) &&
    (!filters.resourceId || reservation.resourceId === filters.resourceId) &&
    (!filters.source || reservation.source === filters.source) &&
    (!filters.memberId || reservation.memberId === filters.memberId) &&
    (!filters.dateFrom || (Number.isFinite(startTime) && startTime >= fromTime)) &&
    (!filters.dateTo || (Number.isFinite(startTime) && startTime <= toTime)) &&
    (!search || haystack.includes(search))
  );
}

function reservationDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "unscheduled";
  }
  return toDateInput(date);
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(date) {
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric"
  }).format(date);
}

function buildMonthCalendarDays(monthDate) {
  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function groupReservationsByDate(reservations) {
  return reservations.reduce((accumulator, reservation) => {
    const key = reservation.startsAt ? reservationDateKey(reservation.startsAt) : "unscheduled";
    const items = accumulator.get(key) || [];
    items.push(reservation);
    accumulator.set(key, items);
    return accumulator;
  }, new Map());
}

function reservationTimeRange(reservation = {}) {
  const starts = new Date(reservation.startsAt);
  const ends = new Date(reservation.endsAt);
  if (Number.isNaN(starts.getTime())) {
    return "Unscheduled";
  }
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  if (Number.isNaN(ends.getTime())) {
    return timeFormatter.format(starts);
  }
  return `${timeFormatter.format(starts)} - ${timeFormatter.format(ends)}`;
}

function formatAvailabilityReason(item = {}) {
  const labels = {
    availability_block_overlap: "Closure or hold",
    duration_too_long: "Duration too long",
    duration_too_short: "Duration too short",
    not_reservable: "Not reservable",
    outside_business_hours: "Outside operating hours",
    outside_resource_availability: "Outside facility hours",
    reservation_overlap: "Already booked",
    reservation_rules_disabled: "Booking rules are off",
    resource_not_open: "Facility not open",
    slot_size_mismatch: "Wrong time increment",
    target_control_not_clear: "Cease-fire active"
  };
  const fallbacks = {
    outside_business_hours: "Choose a time inside the range operating schedule.",
    outside_resource_availability: "Choose a date and time inside this facility's configured window.",
    reservation_overlap: "Choose another facility or time window.",
    target_control_not_clear: "Wait until firing is allowed before reserving this facility."
  };
  return {
    title: labels[item.code] || statusLabel(item.code),
    message: item.message || fallbacks[item.code] || "Review the facility setup and requested booking window."
  };
}

function paymentRequirementLabel(resource = {}) {
  const payment = resource.reservationRules?.payment || {};
  if (!payment.enabled) {
    return "No booking fee";
  }
  const amount = Number(payment.amountCents || 0) / 100;
  const currency = String(payment.currency || "USD").toUpperCase();
  return amount > 0 ? `${new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount)} required` : "Payment required";
}

function ruleDurationPolicyLabel(resource = {}) {
  const rules = resource.reservationRules || {};
  const min = Number(rules.minDurationMinutes || 30);
  const max = Number(rules.maxDurationMinutes || 120);
  return min === max ? `${max} minutes` : `${min} to ${max} minutes`;
}

function ruleSlotPolicyLabel(resource = {}) {
  const slot = Number(resource.reservationRules?.slotSizeMinutes || 30);
  return `Every ${slot} minutes`;
}

function ruleAdvancePolicyLabel(resource = {}) {
  const advance = Number(resource.reservationRules?.advanceBookingDays || 0);
  return advance ? `Up to ${advance} days ahead` : "No advance limit";
}

function ruleActiveLimitPolicyLabel(resource = {}) {
  const activeLimit = Number(resource.reservationRules?.activeReservationLimit || 0);
  return activeLimit ? `${activeLimit} active booking${activeLimit === 1 ? "" : "s"} per member` : "No active booking limit";
}

function ruleEditCutoffPolicyLabel(resource = {}) {
  const editWindow = Number(resource.reservationRules?.cancellationCutoffHours || 0);
  return editWindow ? `Until ${editWindow} hours before start` : "No cutoff";
}

function reservationRuleDraftFromResource(resource = {}) {
  const rules = resource.reservationRules || {};
  const payment = rules.payment || {};
  return {
    reservable: Boolean(resource.reservable && rules.enabled),
    minDurationMinutes: String(rules.minDurationMinutes || 30),
    maxDurationMinutes: String(rules.maxDurationMinutes || 120),
    slotSizeMinutes: String(rules.slotSizeMinutes || 30),
    advanceBookingDays: String(rules.advanceBookingDays || 0),
    activeReservationLimit: String(rules.activeReservationLimit || 0),
    cancellationCutoffHours: String(rules.cancellationCutoffHours || 0),
    paymentEnabled: Boolean(payment.enabled),
    paymentAmount: String(Number(payment.amountCents || 0) / 100),
    paymentCurrency: payment.currency || "usd",
    paymentLabel: payment.label || "Reservation fee"
  };
}

function numberFromDraft(value, fallback = 0) {
  const parsed = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function centsFromDraft(value) {
  const parsed = Number.parseFloat(String(value || "0"));
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0;
}

function reservationRulePatchFromDraft(draft, resource = {}) {
  const existingRules = resource.reservationRules || {};
  const existingPayment = existingRules.payment || {};
  return {
    reservable: Boolean(draft.reservable),
    reservationRules: {
      ...existingRules,
      enabled: Boolean(draft.reservable),
      minDurationMinutes: numberFromDraft(draft.minDurationMinutes, 30),
      maxDurationMinutes: numberFromDraft(draft.maxDurationMinutes, 120),
      slotSizeMinutes: numberFromDraft(draft.slotSizeMinutes, 30),
      advanceBookingDays: numberFromDraft(draft.advanceBookingDays, 0),
      activeReservationLimit: numberFromDraft(draft.activeReservationLimit, 0),
      cancellationCutoffHours: numberFromDraft(draft.cancellationCutoffHours, 0),
      payment: {
        ...existingPayment,
        enabled: Boolean(draft.reservable && draft.paymentEnabled),
        amountCents: centsFromDraft(draft.paymentAmount),
        currency: draft.paymentCurrency || "usd",
        label: draft.paymentLabel || "Reservation fee"
      }
    }
  };
}

function normalizedReservationRuleDraft(draft = {}) {
  return {
    reservable: Boolean(draft.reservable),
    minDurationMinutes: numberFromDraft(draft.minDurationMinutes, 30),
    maxDurationMinutes: numberFromDraft(draft.maxDurationMinutes, 120),
    slotSizeMinutes: numberFromDraft(draft.slotSizeMinutes, 30),
    advanceBookingDays: numberFromDraft(draft.advanceBookingDays, 0),
    activeReservationLimit: numberFromDraft(draft.activeReservationLimit, 0),
    cancellationCutoffHours: numberFromDraft(draft.cancellationCutoffHours, 0),
    paymentEnabled: Boolean(draft.paymentEnabled),
    paymentAmountCents: centsFromDraft(draft.paymentAmount),
    paymentCurrency: draft.paymentCurrency || "usd",
    paymentLabel: draft.paymentLabel || "Reservation fee"
  };
}

function reservationRuleDraftChanged(resource, draft) {
  return JSON.stringify(normalizedReservationRuleDraft(draft)) !==
    JSON.stringify(normalizedReservationRuleDraft(reservationRuleDraftFromResource(resource)));
}

function buildReservationLifecyclePayload(status, actionLabel) {
  const reason = window.prompt(`${actionLabel} reason (optional)`);
  if (reason === null) {
    return null;
  }
  const trimmedReason = reason.trim();
  return trimmedReason ? { status, reason: trimmedReason } : { status };
}

function submitReservationLifecycleUpdate(onUpdateReservation, reservation, status, actionLabel) {
  const payload = buildReservationLifecyclePayload(status, actionLabel);
  if (payload) {
    onUpdateReservation(reservation.reservationId, payload);
  }
}

function FacilitySelectOptions({ childrenByParentId, parentResources, resourceById, depth = 0 }) {
  return parentResources.flatMap((resource) => {
    const children = childrenByParentId.get(resource.resourceId) || [];
    const level = resourceHierarchyLevel(resource, resourceById);
    const prefix = depth > 0 ? `${"\u00a0".repeat(depth * 3)}- ` : "";
    return [
      <option value={resource.resourceId} key={resource.resourceId}>
        {prefix}{hierarchyLevelLabel(level)} - {resourcePathLabel(resource, resourceById)}
      </option>,
      ...(
        children.length > 0
          ? FacilitySelectOptions({
            childrenByParentId,
            parentResources: children,
            resourceById,
            depth: depth + 1
          })
          : []
      )
    ];
  });
}

function ReservationSectionTabs({ activeSection, onSelect, sections }) {
  return (
    <div className="reservations-primary-tabs" role="tablist" aria-label="Reservation sections">
      {sections.map((section) => (
        <button
          type="button"
          key={section.id}
          role="tab"
          aria-selected={activeSection === section.id}
          className={activeSection === section.id ? "is-active" : ""}
          onClick={() => onSelect(section.id)}
        >
          <strong>{section.label}</strong>
          <span>{section.detail}</span>
        </button>
      ))}
    </div>
  );
}

function ReservationFilters({ filters, members, onChange, resourceById, rootResources, childrenByParentId }) {
  function updateFilter(name, value) {
    onChange({ ...filters, [name]: value });
  }

  return (
    <div className="reservations-toolbar">
      <label>
        <span>Search</span>
        <input
          type="search"
          value={filters.q}
          onChange={(event) => updateFilter("q", event.target.value)}
          placeholder="Name, email, facility"
        />
      </label>
      <label>
        <span>Status</span>
        <select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
          <option value="">All statuses</option>
          {RESERVATION_STATUSES.map((status) => (
            <option value={status} key={status}>{statusLabel(status)}</option>
          ))}
        </select>
      </label>
      <label>
        <span>From</span>
        <input
          type="date"
          value={filters.dateFrom}
          onInput={(event) => updateFilter("dateFrom", event.currentTarget.value)}
          onChange={(event) => updateFilter("dateFrom", event.target.value)}
        />
      </label>
      <label>
        <span>To</span>
        <input
          type="date"
          value={filters.dateTo}
          onInput={(event) => updateFilter("dateTo", event.currentTarget.value)}
          onChange={(event) => updateFilter("dateTo", event.target.value)}
        />
      </label>
      <label>
        <span>Facility</span>
        <select value={filters.resourceId} onChange={(event) => updateFilter("resourceId", event.target.value)}>
          <option value="">All facilities</option>
          <FacilitySelectOptions
            childrenByParentId={childrenByParentId}
            parentResources={rootResources}
            resourceById={resourceById}
          />
        </select>
      </label>
      <label>
        <span>Source</span>
        <select value={filters.source} onChange={(event) => updateFilter("source", event.target.value)}>
          <option value="">All sources</option>
          {RESERVATION_SOURCES.map((source) => (
            <option value={source} key={source}>{statusLabel(source)}</option>
          ))}
        </select>
      </label>
      <label>
        <span>Member</span>
        <select value={filters.memberId} onChange={(event) => updateFilter("memberId", event.target.value)}>
          <option value="">All members</option>
          {members.map((member) => (
            <option value={member.memberId} key={member.memberId}>
              {member.fullName || member.email || "Member"}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function ReservationRow({ onOpen, reservation, resource, resourceById }) {
  const attendance = reservation.attendance || {};
  const path = resource ? resourcePathLabel(resource, resourceById) : reservation.resourceSnapshot?.name || "Facility";
  const reservationStatus = reservation.status || "confirmed";
  return (
    <button className={`reservation-list-row status-${reservationStatus}`} type="button" onClick={() => onOpen(reservation.reservationId)}>
      <span className="reservation-row-time">
        <strong>{formatDateTime(reservation.startsAt)}</strong>
        <small>{durationLabel(reservation.startsAt, reservation.endsAt)}</small>
      </span>
      <span className="reservation-row-main">
        <span className="reservation-row-title">
          <strong>{reservationCustomerLabel(reservation)}</strong>
          <span className={`reservation-row-status status-${reservationStatus}`}>{statusLabel(reservationStatus)}</span>
        </span>
        <small>{path}</small>
      </span>
      <span className="reservation-row-meta">
        <strong>{attendance.checkedInAt ? "Checked in" : statusLabel(reservation.source || "owner")}</strong>
        <small>{reservation.contactEmail || reservation.memberSnapshot?.email || "No email recorded"}</small>
      </span>
      <span className="reservation-row-action">Open</span>
    </button>
  );
}

function ReservationDetail({ backLabel = "Back to bookings", onClose, onUpdateReservation, reservation, resource, resources, resourceById, updatingReservationId }) {
  const isUpdating = updatingReservationId === reservation.reservationId;
  const attendance = reservation.attendance || {};
  const reservationStatus = reservation.status || "confirmed";
  const closedStatuses = ["cancelled", "completed", "no_show"];
  const isClosed = closedStatuses.includes(reservationStatus);
  const hasCheckedIn = Boolean(attendance.checkedInAt);
  const hasCheckedOut = Boolean(attendance.checkedOutAt);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    resourceId: reservation.resourceId || "",
    customerName: reservation.customerName || "",
    contactEmail: reservation.contactEmail || "",
    startsAt: isoToDateTimeLocalInput(reservation.startsAt),
    endsAt: isoToDateTimeLocalInput(reservation.endsAt),
    notes: reservation.notes || "",
    overrideAvailability: false,
    overrideReason: "",
    reason: ""
  });

  function submitReservationCorrection(event) {
    event.preventDefault();
    onUpdateReservation(reservation.reservationId, {
      resourceId: editForm.resourceId,
      customerName: editForm.customerName,
      contactEmail: editForm.contactEmail,
      startsAt: new Date(editForm.startsAt).toISOString(),
      endsAt: new Date(editForm.endsAt).toISOString(),
      notes: editForm.notes,
      overrideAvailability: editForm.overrideAvailability,
      overrideReason: editForm.overrideReason,
      reason: editForm.reason
    });
    setEditing(false);
  }

  const actionButtons = [];
  if (!isClosed && !hasCheckedIn) {
    actionButtons.push(
      <button
        type="button"
        className="reservation-primary-action"
        disabled={isUpdating}
        onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_in" })}
      >
        Check in
      </button>
    );
  }
  if (!isClosed && hasCheckedIn && !hasCheckedOut) {
    actionButtons.push(
      <button
        type="button"
        className="reservation-primary-action"
        disabled={isUpdating}
        onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_out" })}
      >
        Check out
      </button>
    );
  }
  actionButtons.push(
    <button type="button" className="secondary-button" disabled={isUpdating} onClick={() => setEditing((current) => !current)}>
      {editing ? "Close edit" : "Edit booking"}
    </button>
  );
  if (!isClosed) {
    actionButtons.push(
      <button
        type="button"
        className="secondary-button"
        disabled={isUpdating}
        onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "cancelled", "Cancel booking")}
      >
        Cancel booking
      </button>,
      <button
        type="button"
        className="secondary-button"
        disabled={isUpdating}
        onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "completed", "Complete booking")}
      >
        Mark complete
      </button>,
      <button
        type="button"
        className="secondary-button"
        disabled={isUpdating}
        onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "no_show", "Mark no-show")}
      >
        Mark no-show
      </button>
    );
  }

  return (
    <div className="reservation-detail-screen">
      <div className="reservation-detail-header">
        <button type="button" className="reservation-back-button" onClick={onClose}>{backLabel}</button>
        <div className="reservation-detail-title">
          <span className={`reservation-detail-status status-${reservationStatus}`}>{statusLabel(reservationStatus)}</span>
          <strong>{reservationCustomerLabel(reservation)}</strong>
          <small>{resource ? resourcePathLabel(resource, resourceById) : reservation.resourceSnapshot?.name || "Facility"}</small>
        </div>
      </div>

      <div className="reservation-detail-grid">
        <div>
          <span>Window</span>
          <strong>{formatWindow(reservation)}</strong>
        </div>
        <div>
          <span>Source</span>
          <strong>{statusLabel(reservation.source || "owner")}</strong>
        </div>
        <div>
          <span>Contact</span>
          <strong>{reservation.contactEmail || reservation.memberSnapshot?.email || "Not recorded"}</strong>
        </div>
        <div>
          <span>Attendance</span>
          <strong>{attendance.status ? statusLabel(attendance.status) : "Not checked in"}</strong>
        </div>
      </div>

      <div className="reservation-action-bar">
        {actionButtons.map((button, index) => (
          <span className="reservation-action-wrapper" key={`${button.props.children}-${index}`}>
            {button}
          </span>
        ))}
      </div>

      {editing && (
        <form className="reservation-edit-form" onSubmit={submitReservationCorrection}>
          <label>
            <span>Facility</span>
            <select
              value={editForm.resourceId}
              onChange={(event) => setEditForm((current) => ({ ...current, resourceId: event.target.value }))}
              required
            >
              {resources.map((item) => (
                <option value={item.resourceId} key={item.resourceId}>
                  {resourcePathLabel(item, resourceById)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Name</span>
            <input value={editForm.customerName} onChange={(event) => setEditForm((current) => ({ ...current, customerName: event.target.value }))} required />
          </label>
          <label>
            <span>Email</span>
            <input type="email" value={editForm.contactEmail} onChange={(event) => setEditForm((current) => ({ ...current, contactEmail: event.target.value }))} />
          </label>
          <label>
            <span>Starts</span>
            <input type="datetime-local" value={editForm.startsAt} onChange={(event) => setEditForm((current) => ({ ...current, startsAt: event.target.value }))} required />
          </label>
          <label>
            <span>Ends</span>
            <input type="datetime-local" value={editForm.endsAt} onChange={(event) => setEditForm((current) => ({ ...current, endsAt: event.target.value }))} required />
          </label>
          <label>
            <span>Notes</span>
            <textarea value={editForm.notes} onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))} />
          </label>
          <label>
            <span>Change reason</span>
            <textarea value={editForm.reason} onChange={(event) => setEditForm((current) => ({ ...current, reason: event.target.value }))} />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={editForm.overrideAvailability}
              onChange={(event) => setEditForm((current) => ({ ...current, overrideAvailability: event.target.checked }))}
            />
            Override availability checks
          </label>
          {editForm.overrideAvailability && (
            <label>
              <span>Override reason</span>
              <input value={editForm.overrideReason} onChange={(event) => setEditForm((current) => ({ ...current, overrideReason: event.target.value }))} />
            </label>
          )}
          <div className="reservation-form-actions">
            <button type="submit" disabled={isUpdating || !editForm.resourceId || !editForm.customerName.trim()}>
              {isUpdating ? "Saving..." : "Save booking"}
            </button>
            <button type="button" className="secondary-button" onClick={() => setEditing(false)}>Cancel edit</button>
          </div>
        </form>
      )}

    </div>
  );
}

function BookingCreatePanel({
  childrenByParentId,
  members,
  onOpenChange,
  onCreateReservation,
  resourceById,
  reservationForm,
  rootResources,
  savingReservation,
  setReservationForm
}) {
  const [open, setOpen] = useState(false);

  function setPanelOpen(nextOpen) {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }

  function handleMemberChange(memberId) {
    const member = members.find((item) => item.memberId === memberId);
    setReservationForm((current) => ({
      ...current,
      memberId,
      customerName: member?.fullName || member?.name || "",
      contactEmail: member?.email || ""
    }));
  }

  const selectedMember = members.find((member) => member.memberId === reservationForm.memberId);

  return (
    <div className={`reservation-create-panel ${open ? "is-open" : ""}`}>
      <button className="reservation-create-trigger" type="button" onClick={() => setPanelOpen(!open)}>
        <span>{open ? "Close form" : "New booking"}</span>
        <strong>{open ? "Booking details" : "Create booking"}</strong>
      </button>
      {open && (
        <form className="reservation-create-form" onSubmit={onCreateReservation}>
          <label>
            <span>Member</span>
            <select
              value={reservationForm.memberId}
              onChange={(event) => handleMemberChange(event.target.value)}
              required
            >
              <option value="">Select member</option>
              {members.map((member) => (
                <option value={member.memberId} key={member.memberId}>
                  {member.fullName || member.name || member.email || "Member"}
                </option>
              ))}
            </select>
          </label>
          <div className="reservation-selected-member">
            <span>Booking for</span>
            <strong>{selectedMember ? selectedMember.fullName || selectedMember.name || selectedMember.email : "Select an existing member"}</strong>
            <small>{selectedMember?.email || "Member details are pulled from the member record."}</small>
          </div>
          <label>
            <span>Facility</span>
            <select
              value={reservationForm.resourceId}
              onChange={(event) => setReservationForm((current) => ({ ...current, resourceId: event.target.value }))}
              required
            >
              <option value="">Select facility</option>
              <FacilitySelectOptions
                childrenByParentId={childrenByParentId}
                parentResources={rootResources}
                resourceById={resourceById}
              />
            </select>
          </label>
          <label>
            <span>Starts</span>
            <input
              type="datetime-local"
              value={reservationForm.startsAt}
              onChange={(event) => setReservationForm((current) => ({ ...current, startsAt: event.target.value }))}
              required
            />
          </label>
          <label>
            <span>Ends</span>
            <input
              type="datetime-local"
              value={reservationForm.endsAt}
              onChange={(event) => setReservationForm((current) => ({ ...current, endsAt: event.target.value }))}
              required
            />
          </label>
          <label>
            <span>Source</span>
            <select
              value={reservationForm.source}
              onChange={(event) => setReservationForm((current) => ({ ...current, source: event.target.value }))}
            >
              <option value="owner">Owner booking</option>
              <option value="walk_in">Walk-in</option>
              <option value="event">Event hold</option>
            </select>
          </label>
          <label>
            <span>Notes</span>
            <input value={reservationForm.notes} onChange={(event) => setReservationForm((current) => ({ ...current, notes: event.target.value }))} />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={reservationForm.overrideAvailability}
              onChange={(event) => setReservationForm((current) => ({ ...current, overrideAvailability: event.target.checked }))}
            />
            Override availability checks
          </label>
          {reservationForm.overrideAvailability && (
            <label>
              <span>Override reason</span>
              <input
                value={reservationForm.overrideReason}
                onChange={(event) => setReservationForm((current) => ({ ...current, overrideReason: event.target.value }))}
                placeholder="Manager approved exception"
              />
            </label>
          )}
          <div className="reservation-form-actions">
            <button type="submit" disabled={savingReservation || !reservationForm.resourceId || !reservationForm.memberId}>
              {savingReservation ? "Creating..." : "Create booking"}
            </button>
            <button type="button" className="secondary-button" onClick={() => setPanelOpen(false)}>Close</button>
          </div>
        </form>
      )}
    </div>
  );
}

function ReservationRulesWorkspace({
  activeBookingsCount,
  facilityResources,
  onUpdateResource,
  reservableResources,
  resourceById,
  updatingResourceId
}) {
  const [selectedRuleResourceId, setSelectedRuleResourceId] = useState("");
  const [ruleFilters, setRuleFilters] = useState({ q: "", level: "", status: "" });
  const [editingRule, setEditingRule] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(null);
  const [savingRules, setSavingRules] = useState(false);
  const selectedRuleResource = facilityResources.find((resource) => resource.resourceId === selectedRuleResourceId);

  useEffect(() => {
    if (selectedRuleResourceId && !selectedRuleResource) {
      setSelectedRuleResourceId("");
      setEditingRule(false);
      setRuleDraft(null);
    }
  }, [selectedRuleResource, selectedRuleResourceId]);

  function openRuleResource(resourceId) {
    const resource = facilityResources.find((item) => item.resourceId === resourceId);
    setSelectedRuleResourceId(resourceId);
    setRuleDraft(resource ? reservationRuleDraftFromResource(resource) : null);
    setEditingRule(false);
  }

  function closeRuleResource() {
    setSelectedRuleResourceId("");
    setEditingRule(false);
    setRuleDraft(null);
  }

  function beginEditingRule() {
    if (!selectedRuleResource) {
      return;
    }
    setRuleDraft(reservationRuleDraftFromResource(selectedRuleResource));
    setEditingRule(true);
  }

  function updateRuleDraft(patch) {
    setRuleDraft((current) => ({
      ...(current || reservationRuleDraftFromResource(selectedRuleResource)),
      ...patch
    }));
  }

  async function saveRuleDraft(event) {
    event.preventDefault();
    if (!onUpdateResource || !selectedRuleResource || !ruleDraft || savingRules) {
      return;
    }
    if (!reservationRuleDraftChanged(selectedRuleResource, ruleDraft)) {
      setEditingRule(false);
      return;
    }

    setSavingRules(true);
    try {
      const updated = await onUpdateResource(selectedRuleResource.resourceId, reservationRulePatchFromDraft(ruleDraft, selectedRuleResource));
      if (updated) {
        setSelectedRuleResourceId(updated.resourceId);
        setRuleDraft(reservationRuleDraftFromResource(updated));
        setEditingRule(false);
      }
    } finally {
      setSavingRules(false);
    }
  }

  const hasRuleChange = Boolean(selectedRuleResource && ruleDraft && reservationRuleDraftChanged(selectedRuleResource, ruleDraft));
  const ruleListResources = useMemo(() => (
    facilityResources.filter((resource) => {
      const level = resourceHierarchyLevel(resource, resourceById);
      const bookable = isResourceBookable(resource);
      const search = String(ruleFilters.q || "").trim().toLowerCase();
      const haystack = [
        resource.name,
        resourcePathLabel(resource, resourceById),
        hierarchyLevelLabel(level),
        bookable ? "members can reserve booking on" : "booking off not available"
      ].filter(Boolean).join(" ").toLowerCase();
      return (
        (!ruleFilters.level || level === ruleFilters.level) &&
        (!ruleFilters.status || (ruleFilters.status === "bookable" ? bookable : !bookable)) &&
        (!search || haystack.includes(search))
      );
    })
  ), [facilityResources, resourceById, ruleFilters]);
  const selectedLevel = selectedRuleResource ? resourceHierarchyLevel(selectedRuleResource, resourceById) : "";
  const selectedDraft = ruleDraft || (selectedRuleResource ? reservationRuleDraftFromResource(selectedRuleResource) : null);
  const selectedDisabled = savingRules || updatingResourceId === selectedRuleResourceId;

  return (
    <div className={`reservation-rules-workspace ${selectedRuleResource ? "has-selection" : ""} ${editingRule ? "is-editing" : ""}`}>
      <div className="reservation-rules-command">
        <div>
          <span>Booking rules</span>
          <strong>
            {selectedRuleResource
              ? resourcePathLabel(selectedRuleResource, resourceById)
              : "Future booking behavior"}
          </strong>
          <small>
            {selectedRuleResource
              ? "Rules here affect future member reservations for this selected facility. Existing bookings will not be changed."
              : "Select one Zone, Range, or Lane to review and edit its booking rules."}
          </small>
        </div>
        <div className="reservation-rules-actions">
          {selectedRuleResource ? (
            <>
              {editingRule ? (
                <>
                  <button
                    type="submit"
                    className="reservation-primary-action"
                    form="reservation-rule-form"
                    disabled={savingRules || Boolean(updatingResourceId) || !hasRuleChange}
                  >
                    {savingRules ? "Saving..." : hasRuleChange ? "Save rules" : "No changes"}
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={savingRules}
                    onClick={() => {
                      setRuleDraft(reservationRuleDraftFromResource(selectedRuleResource));
                      setEditingRule(false);
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" className="reservation-primary-action" onClick={beginEditingRule} disabled={!onUpdateResource}>
                  Edit rules
                </button>
              )}
              <button type="button" className="secondary-button" disabled={savingRules} onClick={closeRuleResource}>
                Back to list
              </button>
            </>
          ) : (
            null
          )}
        </div>
      </div>

      {!selectedRuleResource && (
        <div className="reservation-rules-summary">
          <div>
            <span>Reservable facilities</span>
            <strong>{reservableResources.length}</strong>
          </div>
          <div>
            <span>Active bookings</span>
            <strong>{activeBookingsCount}</strong>
          </div>
          <div>
            <span>Total facilities</span>
            <strong>{facilityResources.length}</strong>
          </div>
          <div>
            <span>Payment rules</span>
            <strong>{reservableResources.filter((resource) => resource.reservationRules?.payment?.enabled).length}</strong>
          </div>
        </div>
      )}

      {selectedRuleResource ? (
        <div className="reservation-rule-detail">
          {!editingRule && (
            <div className="reservation-rule-selected-summary">
              <div>
                <span>Booking status</span>
                <strong>{selectedDraft?.reservable ? "Members can reserve this facility" : "Not available for member booking"}</strong>
              </div>
              <div>
                <span>Visit length</span>
                <strong>{ruleDurationPolicyLabel(selectedRuleResource)}</strong>
              </div>
              <div>
                <span>Booking starts</span>
                <strong>{ruleSlotPolicyLabel(selectedRuleResource)}</strong>
              </div>
              <div>
                <span>Advance booking</span>
                <strong>{ruleAdvancePolicyLabel(selectedRuleResource)}</strong>
              </div>
              <div>
                <span>Member limit</span>
                <strong>{ruleActiveLimitPolicyLabel(selectedRuleResource)}</strong>
              </div>
              <div>
                <span>Edit or cancel</span>
                <strong>{ruleEditCutoffPolicyLabel(selectedRuleResource)}</strong>
              </div>
              <div>
                <span>Payment</span>
                <strong>{paymentRequirementLabel(selectedRuleResource)}</strong>
              </div>
            </div>
          )}

          {editingRule && selectedDraft ? (
            <form className="reservation-rules-editor" id="reservation-rule-form" onSubmit={saveRuleDraft}>
              <article className={`reservation-rule-editor-row ${selectedDraft.reservable ? "is-bookable" : "is-not-bookable"}`}>
                <div className="reservation-rule-editor-heading">
                  <div>
                    <span>{hierarchyLevelLabel(selectedLevel)}</span>
                    <strong>{resourcePathLabel(selectedRuleResource, resourceById)}</strong>
                  </div>
                  <label className="reservation-rule-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedDraft.reservable)}
                      disabled={selectedDisabled}
                      onChange={(event) => updateRuleDraft({ reservable: event.target.checked })}
                    />
                    <span>{selectedDraft.reservable ? "Members can book" : "Booking off"}</span>
                  </label>
                </div>

                <div className="reservation-rule-editor-grid">
                  <label>
                    <span>Min duration</span>
                    <input
                      type="number"
                      min="1"
                      value={selectedDraft.minDurationMinutes}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ minDurationMinutes: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Max duration</span>
                    <input
                      type="number"
                      min="1"
                      value={selectedDraft.maxDurationMinutes}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ maxDurationMinutes: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Time slot</span>
                    <input
                      type="number"
                      min="1"
                      value={selectedDraft.slotSizeMinutes}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ slotSizeMinutes: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Advance window</span>
                    <input
                      type="number"
                      min="0"
                      value={selectedDraft.advanceBookingDays}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ advanceBookingDays: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Active limit</span>
                    <input
                      type="number"
                      min="0"
                      value={selectedDraft.activeReservationLimit}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ activeReservationLimit: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Edit cutoff</span>
                    <input
                      type="number"
                      min="0"
                      value={selectedDraft.cancellationCutoffHours}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ cancellationCutoffHours: event.target.value })}
                    />
                  </label>
                  <label className="reservation-rule-payment-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedDraft.paymentEnabled)}
                      disabled={selectedDisabled || !selectedDraft.reservable}
                      onChange={(event) => updateRuleDraft({ paymentEnabled: event.target.checked })}
                    />
                    <span>Payment required</span>
                  </label>
                  <label>
                    <span>Fee amount</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={selectedDraft.paymentAmount}
                      disabled={selectedDisabled || !selectedDraft.reservable || !selectedDraft.paymentEnabled}
                      onChange={(event) => updateRuleDraft({ paymentAmount: event.target.value })}
                    />
                  </label>
                </div>
              </article>
            </form>
          ) : null}
        </div>
      ) : (
        <>
          <div className="reservation-rules-filterbar">
            <label>
              <span>Search facilities</span>
              <input
                type="search"
                value={ruleFilters.q}
                onChange={(event) => setRuleFilters((current) => ({ ...current, q: event.target.value }))}
                placeholder="Search zone, range, lane"
              />
            </label>
            <label>
              <span>Level</span>
              <select value={ruleFilters.level} onChange={(event) => setRuleFilters((current) => ({ ...current, level: event.target.value }))}>
                <option value="">All levels</option>
                <option value="zone">Zones</option>
                <option value="range">Ranges</option>
                <option value="lane">Lanes</option>
              </select>
            </label>
            <label>
              <span>Booking status</span>
              <select value={ruleFilters.status} onChange={(event) => setRuleFilters((current) => ({ ...current, status: event.target.value }))}>
                <option value="">All facilities</option>
                <option value="bookable">Members can reserve</option>
                <option value="not_bookable">Booking off</option>
              </select>
            </label>
          </div>
          <div className="reservation-rules-list">
            {facilityResources.length === 0 ? (
              <div className="empty-state">Add facilities before configuring booking rules.</div>
            ) : ruleListResources.length === 0 ? (
              <div className="empty-state">No facilities match these filters.</div>
            ) : ruleListResources.map((resource) => {
              const level = resourceHierarchyLevel(resource, resourceById);
              const bookable = isResourceBookable(resource);
              return (
                <button className={`reservation-rule-row reservation-rule-list-button ${bookable ? "is-bookable" : "is-not-bookable"}`} type="button" key={resource.resourceId} onClick={() => openRuleResource(resource.resourceId)}>
                  <div>
                    <span>{hierarchyLevelLabel(level)}</span>
                    <strong>{resourcePathLabel(resource, resourceById)}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong>{bookable ? "Members can reserve" : "Booking off"}</strong>
                  </div>
                  <div>
                    <span>Visit length</span>
                    <strong>{bookable ? `${ruleDurationPolicyLabel(resource)} / ${ruleSlotPolicyLabel(resource)}` : "Not available"}</strong>
                  </div>
                  <div>
                    <span>Payment</span>
                    <strong>{paymentRequirementLabel(resource)}</strong>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function ReservationsPage({
  availabilityForm,
  availabilityResult,
  checkingAvailability,
  checkingReservationConflicts,
  exportingReservations,
  loading,
  members = [],
  onCheckAvailability,
  onCheckReservationConflicts,
  onCreateReservation,
  onExportReservations,
  onUpdateResource,
  onUpdateReservation,
  reservationForm,
  resourceReservationConflicts,
  resourceReservations = [],
  resources = [],
  savingReservation,
  selectedRange,
  setAvailabilityForm,
  setReservationForm,
  updatingResourceId,
  updatingReservationId
}) {
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === "undefined") {
      return "calendar";
    }
    const hash = window.location.hash.replace("#reservations-", "");
    return normalizeReservationSection(hash);
  });
  const [filters, setFilters] = useState({ q: "", status: "", dateFrom: "", dateTo: "", resourceId: "", source: "", memberId: "" });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendarReservationCursorByDay, setCalendarReservationCursorByDay] = useState({});
  const [selectedReservationId, setSelectedReservationId] = useState("");
  const [detailReturnSection, setDetailReturnSection] = useState("bookings");
  const [bookingCreateOpen, setBookingCreateOpen] = useState(false);

  useEffect(() => {
    function syncReservationSectionFromHash() {
      const hash = window.location.hash.replace("#reservations-", "");
      setActiveSection(normalizeReservationSection(hash));
    }
    syncReservationSectionFromHash();
    window.addEventListener("hashchange", syncReservationSectionFromHash);
    return () => window.removeEventListener("hashchange", syncReservationSectionFromHash);
  }, []);

  function changeSection(sectionId) {
    const normalized = normalizeReservationSection(sectionId);
    setActiveSection(normalized);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#reservations-${normalized}`);
    }
  }

  const facilityResources = useMemo(() => resources.filter((resource) => !isArmoryResource(resource)), [resources]);
  const resourceById = useMemo(() => buildResourceMap(facilityResources), [facilityResources]);
  const childrenByParentId = useMemo(() => buildChildrenByParentId(facilityResources), [facilityResources]);
  const rootResources = useMemo(() => facilityResources.filter((resource) => !resource.parentResourceId), [facilityResources]);
  const filteredReservations = useMemo(() => (
    resourceReservations.filter((reservation) => resourceById.has(reservation.resourceId))
      .filter((reservation) => reservationMatchesFilters(reservation, filters, resourceById))
  ), [filters, resourceById, resourceReservations]);
  const selectedMonthKey = monthKey(calendarMonth);
  const calendarDays = useMemo(() => buildMonthCalendarDays(calendarMonth), [calendarMonth]);
  const calendarReservations = useMemo(() => (
    filteredReservations.filter((reservation) => {
      const starts = new Date(reservation.startsAt);
      return !Number.isNaN(starts.getTime()) && monthKey(starts) === selectedMonthKey;
    })
  ), [filteredReservations, selectedMonthKey]);
  const calendarGroups = useMemo(() => groupReservationsByDate(calendarReservations), [calendarReservations]);
  const selectedReservation = useMemo(() => (
    resourceReservations.find((reservation) => reservation.reservationId === selectedReservationId)
  ), [resourceReservations, selectedReservationId]);
  const reservableResources = useMemo(() => facilityResources.filter(isResourceBookable), [facilityResources]);
  const activeBookingsCount = resourceReservations.filter((reservation) => ["confirmed", "standby"].includes(reservation.status)).length;
  const reservationExportFilters = {
    status: filters.status,
    resourceId: filters.resourceId,
    source: filters.source,
    memberId: filters.memberId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    q: filters.q
  };

  return (
    <section className="panel reservations-page" id="reservations">
      <div className="panel-header reservations-page-header">
        <div>
          <p className="section-kicker">{selectedRange?.name || "Range"} operations</p>
          <h2>Reservations</h2>
          <p>Bookings, availability checks, and member booking rules for zones, ranges, and lanes.</p>
        </div>
        <div className="reservation-header-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={checkingReservationConflicts || resourceReservations.length === 0}
            onClick={() => onCheckReservationConflicts(reservationExportFilters)}
          >
            {checkingReservationConflicts ? "Checking..." : "Check conflicts"}
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={exportingReservations || resourceReservations.length === 0}
            onClick={() => onExportReservations(reservationExportFilters)}
          >
            {exportingReservations ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      <ReservationSectionTabs
        activeSection={activeSection}
        onSelect={changeSection}
        sections={[
          { id: "calendar", label: "Calendar", detail: "Monthly booking view" },
          { id: "bookings", label: "Bookings", detail: "List and detail" },
          { id: "availability", label: "Availability", detail: "Check a window" },
          { id: "rules", label: "Rules", detail: "Booking policy" }
        ]}
      />

      {resourceReservationConflicts && (
        <div className={resourceReservationConflicts.summary?.totalConflicts > 0 ? "notice is-error" : "notice"}>
          <strong>{resourceReservationConflicts.summary?.totalConflicts || 0} conflict{resourceReservationConflicts.summary?.totalConflicts === 1 ? "" : "s"}</strong>
          {resourceReservationConflicts.summary?.byResource?.length > 0 && (
            <span>
              {resourceReservationConflicts.summary.byResource.map((item) => `${item.resourceName || "Facility"}: ${item.conflicts}`).join(" / ")}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="empty-state">Loading reservations...</div>
      ) : (
        <>
          {activeSection === "calendar" && (
            <div className="reservation-calendar-workspace">
              <section className="panel organization-calendar-panel reservations-calendar-panel">
                <div className="organization-calendar-header">
                  <div>
                    <span className="section-kicker">Reservation calendar</span>
                    <h3>{monthLabel(calendarMonth)}</h3>
                  </div>
                  <div className="organization-calendar-controls">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        const now = new Date();
                        setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                      }}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="organization-month-grid reservations-month-grid" aria-label={`${monthLabel(calendarMonth)} reservations`}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div className="organization-weekday" key={day}>{day}</div>
                  ))}
                  {calendarDays.map((day) => {
                    const key = reservationDateKey(day);
                    const dayReservations = calendarGroups.get(key) || [];
                    const activeReservationIndex = dayReservations.length > 0
                      ? ((calendarReservationCursorByDay[key] || 0) % dayReservations.length + dayReservations.length) % dayReservations.length
                      : 0;
                    const visibleReservation = dayReservations[activeReservationIndex];
                    const resource = visibleReservation ? resourceById.get(visibleReservation.resourceId) : null;
                    const outsideMonth = monthKey(day) !== selectedMonthKey;
                    const today = key === reservationDateKey(new Date());
                    return (
                      <div
                        className={`organization-calendar-day reservations-calendar-day${outsideMonth ? " is-outside-month" : ""}${today ? " is-today" : ""}${dayReservations.length ? " has-events" : ""}`}
                        key={key}
                      >
                        <div className="organization-calendar-day-label">
                          <strong>{day.getDate()}</strong>
                          <span className="organization-calendar-day-badges">
                            {today && <span>Today</span>}
                          </span>
                        </div>
                        <div className="organization-calendar-events">
                          {visibleReservation && (
                            <button
                              type="button"
                              className={`organization-calendar-event reservations-calendar-event is-${visibleReservation.status || "confirmed"}`}
                              key={visibleReservation.reservationId}
                              onClick={() => {
                                setSelectedReservationId(visibleReservation.reservationId);
                                setDetailReturnSection("calendar");
                                changeSection("bookings");
                              }}
                            >
                              <span>{reservationTimeRange(visibleReservation)}</span>
                              <strong>{reservationCustomerLabel(visibleReservation)}</strong>
                              <small>{resource ? compactResourcePathLabel(resource, resourceById) : visibleReservation.resourceSnapshot?.name || "Facility"}</small>
                            </button>
                          )}
                          {dayReservations.length > 1 && (
                            <div className="organization-calendar-event-pager" aria-label={`${dayReservations.length} bookings on ${key}`}>
                              <button
                                type="button"
                                aria-label={`Previous booking on ${key}`}
                                onClick={() => setCalendarReservationCursorByDay((current) => ({
                                  ...current,
                                  [key]: (((current[key] || 0) - 1 + dayReservations.length) % dayReservations.length)
                                }))}
                              >
                                ‹
                              </button>
                              <span>{activeReservationIndex + 1}/{dayReservations.length}</span>
                              <button
                                type="button"
                                aria-label={`Next booking on ${key}`}
                                onClick={() => setCalendarReservationCursorByDay((current) => ({
                                  ...current,
                                  [key]: (((current[key] || 0) + 1) % dayReservations.length)
                                }))}
                              >
                                ›
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}

          {activeSection === "bookings" && (
            <div className="reservation-bookings-workspace">
              {selectedReservation ? (
                <ReservationDetail
                  backLabel={detailReturnSection === "calendar" ? "Back to calendar" : "Back to bookings"}
                  onClose={() => {
                    setSelectedReservationId("");
                    if (detailReturnSection === "calendar") {
                      changeSection("calendar");
                    }
                    setDetailReturnSection("bookings");
                  }}
                  onUpdateReservation={onUpdateReservation}
                  reservation={selectedReservation}
                  resource={resourceById.get(selectedReservation.resourceId)}
                  resources={facilityResources}
                  resourceById={resourceById}
                  updatingReservationId={updatingReservationId}
                />
              ) : (
                <>
                  <BookingCreatePanel
                    childrenByParentId={childrenByParentId}
                    members={members}
                    onOpenChange={setBookingCreateOpen}
                    onCreateReservation={onCreateReservation}
                    resourceById={resourceById}
                    reservationForm={reservationForm}
                    rootResources={rootResources}
                    savingReservation={savingReservation}
                    setReservationForm={setReservationForm}
                  />
                  {!bookingCreateOpen && (
                    <>
                      <ReservationFilters
                        filters={filters}
                        members={members}
                        onChange={setFilters}
                        resourceById={resourceById}
                        rootResources={rootResources}
                        childrenByParentId={childrenByParentId}
                      />
                      <div className="reservation-list">
                        {resourceReservations.length === 0 ? (
                          <div className="empty-state">No bookings yet.</div>
                        ) : filteredReservations.length === 0 ? (
                          <div className="empty-state">No bookings match these filters.</div>
                        ) : filteredReservations.map((reservation) => (
                          <ReservationRow
                            key={reservation.reservationId}
                            onOpen={(reservationId) => {
                              setDetailReturnSection("bookings");
                              setSelectedReservationId(reservationId);
                            }}
                            reservation={reservation}
                            resource={resourceById.get(reservation.resourceId)}
                            resourceById={resourceById}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {activeSection === "availability" && (
            <div className="reservation-availability-workspace">
              <form className="reservation-availability-form" onSubmit={onCheckAvailability}>
                <label>
                  <span>Facility</span>
                  <select
                    value={availabilityForm.resourceId}
                    onChange={(event) => setAvailabilityForm((current) => ({ ...current, resourceId: event.target.value }))}
                    required
                  >
                    <option value="">Select facility</option>
                    <FacilitySelectOptions
                      childrenByParentId={childrenByParentId}
                      parentResources={rootResources}
                      resourceById={resourceById}
                    />
                  </select>
                </label>
                <label>
                  <span>Starts</span>
                  <input
                    type="datetime-local"
                    value={availabilityForm.startsAt}
                    onChange={(event) => setAvailabilityForm((current) => ({ ...current, startsAt: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  <span>Ends</span>
                  <input
                    type="datetime-local"
                    value={availabilityForm.endsAt}
                    onChange={(event) => setAvailabilityForm((current) => ({ ...current, endsAt: event.target.value }))}
                    required
                  />
                </label>
                <button type="submit" disabled={checkingAvailability || !availabilityForm.resourceId}>
                  {checkingAvailability ? "Checking..." : "Check availability"}
                </button>
              </form>

              <div className="reservation-availability-result">
                {!availabilityResult ? (
                  <div className="empty-state">Choose a facility and time to check availability.</div>
                ) : (
                  <div className={`availability-result ${availabilityResult.available ? "is-available" : "is-unavailable"}`}>
                    <strong>{availabilityResult.available ? "Available" : "Unavailable"}</strong>
                    <span>
                      {availabilityResult.resource?.name || "Selected facility"} / {availabilityResult.durationMinutes} minutes
                    </span>
                    {availabilityResult.reasons?.length > 0 && (
                      <div className="availability-reasons">
                        {availabilityResult.reasons.map((item) => {
                          const copy = formatAvailabilityReason(item);
                          return (
                            <div key={item.code}>
                              <strong>{copy.title}</strong>
                              <span>{copy.message}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {availabilityResult.blockingBlocks?.length > 0 && (
                      <div className="availability-reasons">
                        <strong>Blocking closures or holds</strong>
                        {availabilityResult.blockingBlocks.map((block) => (
                          <div key={block.blockId}>
                            <strong>{block.title}</strong>
                            <span>{statusLabel(block.type)} / {formatWindow(block)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {availabilityResult.blockingReservations?.length > 0 && (
                      <div className="availability-reasons">
                        <strong>Blocking bookings</strong>
                        {availabilityResult.blockingReservations.map((reservation) => (
                          <div key={reservation.reservationId}>
                            <strong>{reservationCustomerLabel(reservation)}</strong>
                            <span>{statusLabel(reservation.status)} / {formatWindow(reservation)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === "rules" && (
            <ReservationRulesWorkspace
              activeBookingsCount={activeBookingsCount}
              facilityResources={facilityResources}
              onUpdateResource={onUpdateResource}
              reservableResources={reservableResources}
              resourceById={resourceById}
              updatingResourceId={updatingResourceId}
            />
          )}
        </>
      )}
    </section>
  );
}
